/**
 * A minha conta — dados da sessão e mudança de palavra-passe.
 *
 * Existe porque as Definições são só para admin: o supervisor não tinha
 * onde mudar a própria palavra-passe. Uma conta a que se entrega uma
 * palavra-passe temporária e que depois não a consegue trocar é uma conta
 * com uma palavra-passe partilhada para sempre.
 *
 * Aqui só se mexe na própria conta — as definições da plataforma continuam
 * reservadas ao admin.
 */
import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCog, KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { changeMyPassword } from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../hooks/useToast';

const PAPEIS = {
  super_admin: 'Administrador principal',
  admin: 'Administrador',
  supervisor: 'Supervisor',
};

const AccountPage = () => {
  const { user, logout } = useContext(AuthContext);
  const { success, error } = useToast();
  const navigate = useNavigate();
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy] = useState(false);

  const submeter = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) { error('A nova palavra-passe e a confirmação não coincidem'); return; }
    if (pw.next.length < 8 || !/[A-Z]/.test(pw.next) || !/[a-z]/.test(pw.next) || !/[0-9]/.test(pw.next)) {
      error('Mínimo 8 caracteres, com maiúscula, minúscula e número'); return;
    }
    if (pw.next === pw.current) { error('A nova palavra-passe tem de ser diferente da actual'); return; }
    setBusy(true);
    try {
      await changeMyPassword(pw.current, pw.next);
      setPw({ current: '', next: '', confirm: '' });
      success('Palavra-passe alterada. Vamos pedir que entre de novo.');
      // RN-061: a sessão actual é invalidada do lado do servidor.
      setTimeout(() => { logout(); navigate('/'); }, 1800);
    } catch (err) {
      error(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Erro ao alterar a palavra-passe');
      setBusy(false);
    }
  };

  const campo = (rotulo, chave, autoComplete) => (
    <div>
      <label htmlFor={`pw-${chave}`} className="block text-sm text-slate-400 mb-1">{rotulo}</label>
      <input
        id={`pw-${chave}`}
        type="password"
        autoComplete={autoComplete}
        required
        value={pw[chave]}
        onChange={(e) => setPw((v) => ({ ...v, [chave]: e.target.value }))}
        className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm outline-none focus:border-blue-500"
      />
    </div>
  );

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <UserCog className="w-6 h-6 text-blue-400" /> A minha conta
        </h1>
        <p className="text-sm text-slate-400 mt-1">Os seus dados de acesso à área de gestão.</p>
      </div>

      <section className="rounded-xl border border-slate-800 bg-slate-800/40 p-5">
        <dl className="grid grid-cols-[minmax(90px,auto)_minmax(0,1fr)] gap-x-4 gap-y-3 text-sm">
          <dt className="text-slate-500">Nome</dt>
          <dd className="text-slate-200">{user?.full_name || '—'}</dd>
          <dt className="text-slate-500">Email</dt>
          <dd className="text-slate-200 break-all">{user?.email || '—'}</dd>
          <dt className="text-slate-500">Papel</dt>
          <dd className="text-slate-200">{PAPEIS[user?.role] || user?.role || '—'}</dd>
        </dl>
      </section>

      <section className="rounded-xl border border-slate-800 bg-slate-800/40 p-5">
        <h2 className="text-base font-semibold text-white flex items-center gap-2 mb-1">
          <KeyRound className="w-4 h-4 text-blue-400" /> Alterar palavra-passe
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          Mínimo 8 caracteres, com maiúscula, minúscula e número. Ao mudar, a sessão actual termina.
        </p>
        <form onSubmit={submeter} className="space-y-3">
          {campo('Palavra-passe actual', 'current', 'current-password')}
          {campo('Nova palavra-passe', 'next', 'new-password')}
          {campo('Confirmar nova palavra-passe', 'confirm', 'new-password')}
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-semibold"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Alterar palavra-passe
          </button>
        </form>
      </section>
    </div>
  );
};

export default AccountPage;
